import AuthService from "../../../api/services/AuthService";
import { spotMeActivityItemsFromResponse } from "../../../api/services/spotMeActivityQuery";
import { flattenOrderHistoryApiItemsToUiRows } from "./spotMeActivityHelpers";

export const EXPORT_KIND_SPOT_TRADES = "spot-trades";
export const EXPORT_KIND_FUTURES_TRADES = "futures-trades";
export const EXPORT_KIND_FUTURES_POSITIONS = "futures-positions";

const SPOT_TRADE_EXPORT_SPEC = [
  { key: "executed_at", label: "Time" },
  { key: "pair", label: "Pair" },
  { key: "side", label: "Side" },
  { key: "price", label: "Price" },
  { key: "quantity", label: "Quantity" },
  { key: "quote_quantity", label: "Quote qty" },
  { key: "fee", label: "Fee" },
  { key: "fee_asset", label: "Fee asset" },
  { key: "tds", label: "TDS" },
  { key: "tds_rate", label: "TDS rate" },
  { key: "trade_id", label: "Trade ID" },
  { key: "order_id", label: "Order ID" },
];

const FUTURES_TRADE_EXPORT_SPEC = [
  { key: "executed_at", label: "Time" },
  { key: "symbol", label: "Contract" },
  { key: "side", label: "Side" },
  { key: "order_id", label: "Order ID" },
  { key: "execution_quantity", label: "Size" },
  { key: "execution_price", label: "Fill Price" },
  { key: "fee", label: "Fee" },
  { key: "fee_asset", label: "Fee asset" },
  { key: "realized_pnl", label: "Realized PnL" },
  { key: "role", label: "Role" },
];

const FUTURES_POSITION_EXPORT_SPEC = [
  { key: "closed_at", label: "Close Time" },
  { key: "symbol", label: "Contract" },
  { key: "side", label: "Side" },
  { key: "status", label: "Status" },
  { key: "entry_price", label: "Entry Price" },
  { key: "exit_price", label: "Avg Exit Price" },
  { key: "closed_quantity", label: "Cumulative Closed" },
  { key: "max_quantity", label: "Peak Position" },
  { key: "realized_pnl", label: "Realized Return" },
  { key: "realized_pnl_pct", label: "Return %" },
];

const MONGO_SCALAR_KEYS = [
  "$numberDecimal",
  "$numberLong",
  "$numberInt",
  "$numberDouble",
  "$oid",
  "$date",
  "$timestamp",
];

function unwrapExportValue(val, depth = 0) {
  if (val == null) return "";
  if (depth > 8) return "";
  const t = typeof val;
  if (t === "string") return val;
  if (t === "number") return Number.isFinite(val) ? String(val) : "";
  if (t === "boolean") return val ? "true" : "false";
  if (t === "bigint") return String(val);
  if (val instanceof Date) {
    return Number.isNaN(val.getTime()) ? "" : val.toISOString();
  }
  if (Array.isArray(val)) {
    return val
      .map((item) => unwrapExportValue(item, depth + 1))
      .filter((s) => s !== "")
      .join("; ");
  }
  if (t !== "object") return String(val);

  for (const key of MONGO_SCALAR_KEYS) {
    if (Object.prototype.hasOwnProperty.call(val, key) || key in val) {
      return unwrapExportValue(val[key], depth + 1);
    }
  }

  try {
    if (typeof val.toJSON === "function") {
      const jsoned = val.toJSON();
      if (jsoned !== val) return unwrapExportValue(jsoned, depth + 1);
    }
  } catch {
    // ignore
  }

  try {
    if (typeof val.toString === "function" && val.toString !== Object.prototype.toString) {
      const custom = val.toString();
      if (custom && custom !== "[object Object]") return String(custom);
    }
  } catch {
    // ignore
  }

  for (const key of ["value", "amount", "price", "quantity", "qty", "seconds"]) {
    if (val[key] != null) return unwrapExportValue(val[key], depth + 1);
  }

  const keys = Object.keys(val).filter((k) => val[k] != null);
  if (keys.length === 1) return unwrapExportValue(val[keys[0]], depth + 1);

  return "";
}

function stringifyExportCell(val) {
  const str = unwrapExportValue(val);
  return str === "[object Object]" ? "" : str;
}

function csvEscapeCell(val) {
  const str = stringifyExportCell(val);
  if (/[",\r\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

function isTdsAmountZero(v) {
  if (v === null || v === undefined || v === "") return true;
  const n = typeof v === "number" ? v : parseFloat(String(v).trim());
  return !Number.isNaN(n) && n === 0;
}

function spotTradeCellValue(col, row) {
  if (col.key === "tds") {
    return isTdsAmountZero(row.tds) ? "---" : stringifyExportCell(row.tds);
  }
  return stringifyExportCell(row[col.key]);
}

function decExport(v) {
  return stringifyExportCell(v);
}

function futuresExecutionUserView(ex) {
  const uid = typeof localStorage !== "undefined" ? localStorage.getItem("userId") : null;
  const takerSide = String(ex.side ?? ex.taker_side ?? "").toUpperCase();
  const flip = (s) => (s === "BUY" ? "SELL" : s === "SELL" ? "BUY" : s);
  const isTaker = uid && ex.taker_user_id && String(ex.taker_user_id) === String(uid);
  const isMaker = uid && ex.maker_user_id && String(ex.maker_user_id) === String(uid);
  if (isMaker && !isTaker) {
    return {
      side: flip(takerSide) || stringifyExportCell(ex.side),
      role: "Maker",
      orderId: ex.maker_order_id || ex.order_id || "",
      fee: ex.maker_fee,
      pnl: ex.maker_realized_pnl,
    };
  }
  return {
    side: takerSide || stringifyExportCell(ex.side),
    role: isTaker || ex.taker_role ? "Taker" : stringifyExportCell(ex.role) || "Taker",
    orderId: ex.taker_order_id || ex.maker_order_id || ex.order_id || "",
    fee: ex.taker_fee ?? ex.fee,
    pnl: ex.taker_realized_pnl ?? ex.realized_pnl,
  };
}

function futuresTradeCellValue(col, row) {
  const view = futuresExecutionUserView(row);
  if (col.key === "executed_at") {
    return stringifyExportCell(row.executed_at ?? row.created_at ?? row.timestamp);
  }
  if (col.key === "symbol") {
    return stringifyExportCell(String(row.symbol || "").replace(/-PERP(ETUAL)?$/i, "") || row.symbol);
  }
  if (col.key === "order_id") return stringifyExportCell(view.orderId);
  if (col.key === "side") return stringifyExportCell(view.side);
  if (col.key === "role") return stringifyExportCell(view.role);
  if (col.key === "execution_price") return decExport(row.execution_price ?? row.price);
  if (col.key === "execution_quantity") return decExport(row.execution_quantity ?? row.quantity);
  if (col.key === "fee") return decExport(view.fee);
  if (col.key === "fee_asset") return stringifyExportCell(row.fee_asset || row.settlement_asset);
  if (col.key === "realized_pnl") return decExport(view.pnl);
  return stringifyExportCell(row[col.key]);
}

function futuresPositionCellValue(col, row) {
  if (col.key === "closed_at") {
    return stringifyExportCell(row.closed_at ?? row.updated_at ?? row.created_at);
  }
  return stringifyExportCell(row[col.key]);
}

export function getExportColumnSpec(exportKind) {
  if (exportKind === EXPORT_KIND_FUTURES_TRADES) return FUTURES_TRADE_EXPORT_SPEC;
  if (exportKind === EXPORT_KIND_FUTURES_POSITIONS) return FUTURES_POSITION_EXPORT_SPEC;
  return SPOT_TRADE_EXPORT_SPEC;
}

export function getExportTitle(exportKind) {
  if (exportKind === EXPORT_KIND_FUTURES_TRADES) return "AGCX Futures Trade History";
  if (exportKind === EXPORT_KIND_FUTURES_POSITIONS) return "AGCX Position History";
  return "AGCX Trade History";
}

export function getExportCsvBasename(exportKind) {
  if (exportKind === EXPORT_KIND_FUTURES_TRADES) return "AGCX Futures Trade History.csv";
  if (exportKind === EXPORT_KIND_FUTURES_POSITIONS) return "AGCX Position History.csv";
  return "AGCX Trade History.csv";
}

function cellValueForKind(exportKind, col, row) {
  if (exportKind === EXPORT_KIND_FUTURES_TRADES) return futuresTradeCellValue(col, row);
  if (exportKind === EXPORT_KIND_FUTURES_POSITIONS) return futuresPositionCellValue(col, row);
  return spotTradeCellValue(col, row);
}

export function rowsToCsv(exportKind, items) {
  const spec = getExportColumnSpec(exportKind);
  const labels = spec.map((c) => csvEscapeCell(c.label));
  const body = (items || []).map((row) =>
    spec.map((col) => csvEscapeCell(cellValueForKind(exportKind, col, row))).join(","),
  );
  return [labels.join(","), ...body].join("\r\n");
}

export function getExportCellText(exportKind, col, row) {
  return cellValueForKind(exportKind, col, row);
}

function toIsoFrom(ymd) {
  return ymd ? `${ymd}T00:00:00.000Z` : undefined;
}

function toIsoTo(ymd) {
  return ymd ? `${ymd}T23:59:59.999Z` : undefined;
}

function extractFuturesTradesFromResponse(res) {
  if (!res) return { items: [], total: 0 };
  const nested = res.data;
  const items = Array.isArray(nested?.trades)
    ? nested.trades
    : Array.isArray(res.trades)
      ? res.trades
      : Array.isArray(nested)
        ? nested
        : [];
  const total = Number(nested?.total ?? nested?.pagination?.total ?? res.total ?? items.length);
  return { items, total: Number.isFinite(total) ? total : items.length };
}

function extractFuturesPositionsFromResponse(res) {
  if (!res) return { items: [], total: 0 };
  const nested = res.data;
  const items = Array.isArray(nested?.positions)
    ? nested.positions
    : Array.isArray(res.positions)
      ? res.positions
      : Array.isArray(nested)
        ? nested
        : [];
  const total = Number(nested?.pagination?.total ?? nested?.total ?? res.total ?? items.length);
  return { items, total: Number.isFinite(total) ? total : items.length };
}

function recordKey(row, fallback) {
  if (!row || typeof row !== "object") return String(fallback);
  const raw = row._id ?? row.execution_id ?? row.trade_id ?? row.position_id ?? row.id;
  if (raw == null || raw === "") return String(fallback);
  if (typeof raw === "object" && raw.$oid != null) return String(raw.$oid);
  return String(raw);
}

function appendUnique(all, items, seen) {
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const key = recordKey(item, `${all.length}:${i}`);
    if (seen.has(key)) continue;
    seen.add(key);
    all.push(item);
  }
}

async function fetchAllFuturesTrades(from, to) {
  const pageSize = 100;
  const all = [];
  const seen = new Set();
  let skip = 0;
  let total = Infinity;
  let pages = 0;
  const maxPages = 10000;

  while (skip < total && pages < maxPages) {
    pages += 1;
    const res = await AuthService.futuresExecutions({
      from: toIsoFrom(from),
      to: toIsoTo(to),
      skip,
      limit: pageSize,
    });
    if (!res || res.success === false) {
      if (!all.length) return { ok: false, message: res?.message || "Export failed.", items: [] };
      break;
    }
    const { items, total: reported } = extractFuturesTradesFromResponse(res);
    if (Number.isFinite(reported) && reported >= 0) total = reported;
    if (!items.length) break;
    appendUnique(all, items, seen);
    skip += items.length;
    if (items.length < pageSize) break;
  }

  return {
    ok: true,
    items: all,
    meta: { row_count: all.length, total: Number.isFinite(total) && total !== Infinity ? total : all.length },
  };
}

async function fetchAllFuturesPositions(from, to) {
  const pageSize = 100;
  const all = [];
  const seen = new Set();
  let skip = 0;
  let total = Infinity;
  let pages = 0;
  const maxPages = 10000;

  while (skip < total && pages < maxPages) {
    pages += 1;
    const res = await AuthService.futuresPositionHistory({
      from: toIsoFrom(from),
      to: toIsoTo(to),
      skip,
      limit: pageSize,
    });
    if (!res || res.success === false) {
      if (!all.length) return { ok: false, message: res?.message || "Export failed.", items: [] };
      break;
    }
    const { items, total: reported } = extractFuturesPositionsFromResponse(res);
    if (Number.isFinite(reported) && reported >= 0) total = reported;
    if (!items.length) break;
    appendUnique(all, items, seen);
    skip += items.length;
    if (items.length < pageSize) break;
  }

  return {
    ok: true,
    items: all,
    meta: { row_count: all.length, total: Number.isFinite(total) && total !== Infinity ? total : all.length },
  };
}

async function fetchAllSpotTrades(from, to) {
  const pageSize = 100;
  const all = [];
  const seen = new Set();
  let page = 1;
  const maxPages = 10000;

  while (page <= maxPages) {
    const res = await AuthService.spotMeTrades({
      page,
      page_size: pageSize,
      date_from: from || undefined,
      date_to: to || undefined,
    });
    if (!res || res.success === false) {
      if (!all.length) return { ok: false, message: res?.message || "Export failed.", items: [] };
      break;
    }
    const items = spotMeActivityItemsFromResponse(res);
    if (!items.length) break;
    appendUnique(all, items, seen);
    if (items.length < pageSize) break;
    page += 1;
  }

  return {
    ok: true,
    items: all,
    meta: { row_count: all.length, total: all.length },
  };
}

export async function fetchExportItems(exportKind, from, to) {
  if (exportKind === EXPORT_KIND_SPOT_TRADES) {
    return fetchAllSpotTrades(from, to);
  }
  if (exportKind === EXPORT_KIND_FUTURES_TRADES) {
    return fetchAllFuturesTrades(from, to);
  }
  if (exportKind === EXPORT_KIND_FUTURES_POSITIONS) {
    return fetchAllFuturesPositions(from, to);
  }
  return { ok: false, message: "Unsupported export type.", items: [] };
}

/** Map spot order-history API rows to a simple CSV (client-side fallback). */
export async function fetchSpotOrderHistoryRows(from, to, limit = 10000) {
  const res = await AuthService.spotMeOrdersHistory({
    page: 1,
    page_size: limit,
    date_from: from || undefined,
    date_to: to || undefined,
  });
  const items = spotMeActivityItemsFromResponse(res);
  return flattenOrderHistoryApiItemsToUiRows(items);
}

export function spotOrderHistoryRowsToCsv(rows) {
  const spec = [
    { key: "time", label: "Time" },
    { key: "side", label: "Side" },
    { key: "pairs", label: "Pair" },
    { key: "price", label: "Price" },
    { key: "fillPrice", label: "Fill Price" },
    { key: "orderQty", label: "Order Quantity" },
    { key: "pendingAmount", label: "Pending" },
    { key: "filledBase", label: "Filled (qty)" },
    { key: "filledQuote", label: "Filled (quote)" },
    { key: "fee", label: "Fee" },
    { key: "statusDisplay", label: "Status" },
  ];
  const labels = spec.map((c) => csvEscapeCell(c.label));
  const body = (rows || []).map((row) =>
    spec.map((col) => csvEscapeCell(row[col.key])).join(","),
  );
  return [labels.join(","), ...body].join("\r\n");
}
